import React, { createContext, useContext, useState, useEffect } from 'react';
import { Tour } from '../types';

export type AssistantMode = 'hidden' | 'minimized' | 'initial' | 'welcome-back' | 'enquiry' | 'category-select' | 'group-size' | 'budget-select' | 'suggestions';

export interface UserPreferences {
    category?: string;
    groupSize?: { adults: number; children: number };
    budget?: string; // 'luxury', 'standard', 'budget'
    savedAt?: number;
}

export interface LastViewedTour {
    tourId: string;
    title: string;
    image: string;
    categoryId: string;
    timestamp: number;
}

interface AssistantContextType {
    isVisible: boolean;
    mode: AssistantMode;
    lastViewed: LastViewedTour | null;
    suggestions: Tour[];
    userPreferences: UserPreferences;
    setMode: (mode: AssistantMode) => void;
    setIsVisible: (visible: boolean) => void;
    trackTourView: (tour: Tour) => void;
    setSuggestions: (tours: Tour[]) => void;
    setUserPreferences: (prefs: UserPreferences) => void;
    minimize: () => void;
    maximize: () => void;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [mode, setMode] = useState<AssistantMode>('hidden');
    const [lastViewed, setLastViewed] = useState<LastViewedTour | null>(null);
    const [suggestions, setSuggestions] = useState<Tour[]>([]);
    const [userPreferences, setUserPreferences] = useState<UserPreferences>({});

    useEffect(() => {
        // Hydrate last viewed tour from storage
        const stored = localStorage.getItem('sighttrail_last_viewed_tour');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Check if recent (e.g., last 7 days)
                const isRecent = (Date.now() - parsed.timestamp) < (7 * 24 * 60 * 60 * 1000);
                if (isRecent) {
                    setLastViewed(parsed);
                }
            } catch (e) {
                console.error("Failed to parse last viewed tour", e);
            }
        }

        // Hydrate user preferences from storage
        const storedPrefs = localStorage.getItem('sighttrail_user_preferences');
        if (storedPrefs) {
            try {
                const parsed = JSON.parse(storedPrefs);
                setUserPreferences(parsed);
            } catch (e) {
                console.error("Failed to parse user preferences", e);
            }
        }

        // Assistant auto-trigger is disabled — it now opens only when
        // the user taps the FAB button. Preferences are still hydrated above.
        // The Safari Assistant chat is fully preserved and functional.

    }, []);

    // onboardingTourCompleted event no longer triggers the assistant automatically.
    // The assistant is fully manual (FAB-button-only).


    const trackTourView = (tour: Tour) => {
        const entry: LastViewedTour = {
            tourId: tour.id,
            title: tour.title,
            image: tour.images && tour.images.length > 0 ? tour.images[0] : '',
            categoryId: (tour as any).categoryId || (tour as any).category || '',
            timestamp: Date.now()
        };
        setLastViewed(entry);
        localStorage.setItem('sighttrail_last_viewed_tour', JSON.stringify(entry));

        // If they are viewing a tour, maybe minimize the assistant so it doesn't annoy
        setMode('minimized');
    };

    const minimize = () => setMode('minimized');
    const maximize = () => {
        if (lastViewed) setMode('welcome-back');
        else setMode('initial');
    };

    return (
        <AssistantContext.Provider value={{
            isVisible,
            mode,
            lastViewed,
            suggestions,
            userPreferences,
            setMode,
            setIsVisible,
            trackTourView,
            setSuggestions,
            setUserPreferences: (prefs: UserPreferences) => {
                setUserPreferences(prefs);
                localStorage.setItem('sighttrail_user_preferences', JSON.stringify(prefs));
            },
            minimize,
            maximize
        }}>
            {children}
        </AssistantContext.Provider>
    );
};

export const useAssistant = () => {
    const context = useContext(AssistantContext);
    if (context === undefined) {
        throw new Error('useAssistant must be used within an AssistantProvider');
    }
    return context;
};
